import { useState, useEffect } from 'react';
import { RefreshCw, Upload, ChevronDown, ChevronRight, AlertTriangle, TrendingUp, TrendingDown, FileCheck, CheckCircle, XCircle, HelpCircle } from 'lucide-react';
import { fetchApi } from '../api/client';

export default function TaxLots() {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [selectedTicker, setSelectedTicker] = useState('');
  const [expandedTickers, setExpandedTickers] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [importContent, setImportContent] = useState('');
  const [importAccount, setImportAccount] = useState('fidelity_brokeragelink');
  const [importing, setImporting] = useState(false);
  
  // Reconciliation state
  const [showReconcile, setShowReconcile] = useState(false);
  const [reconcileContent, setReconcileContent] = useState('');
  const [reconcileAccount, setReconcileAccount] = useState('fidelity_brokeragelink');
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResults, setReconcileResults] = useState(null);
  
  // Realized Gains import state (per-lot format)
  const [showClosedImport, setShowClosedImport] = useState(false);
  const [closedImportContent, setClosedImportContent] = useState('');
  const [closedImportAccount, setClosedImportAccount] = useState('fidelity_brokeragelink');
  const [closedImporting, setClosedImporting] = useState(false);
  const [closedImportResult, setClosedImportResult] = useState(null);

  const accounts = [
    { id: '', label: 'All Accounts' },
    { id: 'fidelity_brokeragelink', label: 'BrokerageLink (401k)' },
    { id: 'fidelity_taxable', label: 'Fidelity Taxable' },
    { id: 'fidelity_rollover_ira', label: 'Rollover IRA' },
  ];

  useEffect(() => {
    fetchLots();
  }, [selectedAccount, selectedTicker]);

  const fetchLots = async () => {
    setLoading(true);
    try {
      let url = `/lots?open_only=true`;
      if (selectedAccount) url += `&account_id=${selectedAccount}`;
      if (selectedTicker) url += `&ticker=${selectedTicker}`;
      
      const data = await fetchApi(url);
      setLots(data.lots || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!importContent.trim()) return;
    
    setImporting(true);
    try {
      const data = await fetchApi('/lots/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: importContent,
          account_id: importAccount,
          replace: true,
        }),
      });
      
      if (data.success) {
        alert(`Imported ${data.lots_created} lots (${data.drip_lots} DRIP)`);
        setShowImport(false);
        setImportContent('');
        fetchLots();
      } else {
        alert(`Import failed: ${data.error}`);
      }
    } catch (err) {
      alert(`Import error: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  const handleReconcile = async (autoClose = false) => {
    if (!reconcileContent.trim()) return;
    
    setReconciling(true);
    try {
      const data = await fetchApi('/lots/reconcile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: reconcileContent,
          account_id: reconcileAccount,
          auto_close: autoClose,
        }),
      });
      
      if (data.success) {
        setReconcileResults(data);
        if (autoClose && data.auto_closed > 0) {
          fetchLots(); // Refresh lots after closing
        }
      } else {
        alert(`Reconciliation failed: ${data.error}`);
      }
    } catch (err) {
      alert(`Reconciliation error: ${err.message}`);
    } finally {
      setReconciling(false);
    }
  };

  const clearReconciliation = () => {
    setReconcileResults(null);
    setReconcileContent('');
    setShowReconcile(false);
  };

  // Import Closed Positions (directly to realized_gains table)
  const handleClosedImport = async () => {
    if (!closedImportContent.trim()) return;
    
    setClosedImporting(true);
    try {
      const data = await fetchApi('/lots/realized-gains/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: closedImportContent,
          account_id: closedImportAccount,
          create_ghost_lots: true,
        }),
      });
      
      if (data.success) {
        setClosedImportResult(data);
      } else {
        alert(`Import failed: ${data.error}`);
      }
    } catch (err) {
      alert(`Import error: ${err.message}`);
    } finally {
      setClosedImporting(false);
    }
  };

  const clearClosedImport = () => {
    setClosedImportResult(null);
    setClosedImportContent('');
    setShowClosedImport(false);
  };

  // Group lots by ticker
  const lotsByTicker = lots.reduce((acc, lot) => {
    if (!acc[lot.ticker]) {
      acc[lot.ticker] = [];
    }
    acc[lot.ticker].push(lot);
    return acc;
  }, {});

  // Calculate summary for each ticker
  const tickerSummaries = Object.entries(lotsByTicker).map(([ticker, tickerLots]) => {
    const totalShares = tickerLots.reduce((sum, l) => sum + l.remaining_shares, 0);
    const totalCost = tickerLots.reduce((sum, l) => sum + (l.remaining_shares * l.cost_basis_per_share), 0);
    const shortTerm = tickerLots.filter(l => l.term === 'short');
    const longTerm = tickerLots.filter(l => l.term === 'long');
    const dripLots = tickerLots.filter(l => l.is_drip);
    
    return {
      ticker,
      lots: tickerLots,
      totalShares,
      totalCost,
      avgCost: totalShares > 0 ? totalCost / totalShares : 0,
      shortTermCount: shortTerm.length,
      longTermCount: longTerm.length,
      dripCount: dripLots.length,
    };
  }).sort((a, b) => b.totalCost - a.totalCost);

  const toggleTicker = (ticker) => {
    const newExpanded = new Set(expandedTickers);
    if (newExpanded.has(ticker)) {
      newExpanded.delete(ticker);
    } else {
      newExpanded.add(ticker);
    }
    setExpandedTickers(newExpanded);
  };

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Tax Lots</h2>
          <p className="text-slate-400 text-sm mt-1">
            {lots.length} open lots across {Object.keys(lotsByTicker).length} securities
          </p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <button
            onClick={() => { setShowClosedImport(!showClosedImport); setShowReconcile(false); setShowImport(false); }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-white transition-colors"
          >
            <TrendingUp size={18} />
            Import Realized Gains
          </button>
          <button
            onClick={() => { setShowReconcile(!showReconcile); setShowImport(false); setShowClosedImport(false); }}
            className="flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white transition-colors"
          >
            <FileCheck size={18} />
            Reconcile Sales
          </button>
          <button
            onClick={() => { setShowImport(!showImport); setShowReconcile(false); setShowClosedImport(false); }}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-white transition-colors"
          >
            <Upload size={18} />
            Import Lots
          </button>
          <button
            onClick={fetchLots}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
          >
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Import Panel */}
      {showImport && (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Import Tax Lots from TSV</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-2">Target Account</label>
              <select
                value={importAccount}
                onChange={(e) => setImportAccount(e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white"
              >
                <option value="fidelity_brokeragelink">BrokerageLink (401k)</option>
                <option value="fidelity_taxable">Fidelity Taxable</option>
                <option value="fidelity_rollover_ira">Rollover IRA</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-2">
                Paste TSV Content (from browser scraper)
              </label>
              <textarea
                value={importContent}
                onChange={(e) => setImportContent(e.target.value)}
                placeholder="Symbol&#9;Acquired&#9;Term&#9;..."
                rows={8}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white font-mono text-sm"
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleImport}
                disabled={importing || !importContent.trim()}
                className="px-4 py-2 bg-green-600 hover:bg-green-500 disabled:bg-slate-600 rounded-lg text-white transition-colors"
              >
                {importing ? 'Importing...' : 'Import (Replace Existing)'}
              </button>
              <button
                onClick={() => setShowImport(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
              >
                Cancel
              </button>
            </div>
            <p className="text-xs text-slate-500">
              ⚠️ This will replace all existing lots for the selected account
            </p>
          </div>
        </div>
      )}

      {/* Import Realized Gains Panel (Closed Positions) */}
      {showClosedImport && (
        <div className="bg-slate-800/50 border border-emerald-700/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">Import Realized Gains</h3>
          <p className="text-sm text-slate-400 mb-4">
            Import "Closed Positions" or "Realized Gains YTD" export from Fidelity. 
            This records all your sales and their gains/losses for performance tracking.
          </p>
          
          {!closedImportResult ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Account</label>
                <select
                  value={closedImportAccount}
                  onChange={(e) => setClosedImportAccount(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white"
                >
                  <option value="fidelity_brokeragelink">BrokerageLink (401k)</option>
                  <option value="fidelity_taxable">Fidelity Taxable</option>
                  <option value="fidelity_rollover_ira">Rollover IRA</option>
                  <option value="ameriprise_advisory">Ameriprise Advisory</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Upload CSV or paste content
                </label>
                
                {/* File Upload */}
                <div className="mb-3">
                  <label className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 border-2 border-dashed border-slate-600 hover:border-emerald-500 rounded-lg cursor-pointer transition-colors">
                    <Upload size={20} className="text-slate-400" />
                    <span className="text-slate-400">
                      {closedImportContent ? '✓ CSV loaded - click to replace' : 'Click to upload CSV file'}
                    </span>
                    <input
                      type="file"
                      accept=".csv,.txt"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = (event) => {
                            setClosedImportContent(event.target?.result || '');
                          };
                          reader.readAsText(file);
                        }
                      }}
                    />
                  </label>
                </div>
                
                {/* Or paste manually */}
                <details className="group">
                  <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-400">
                    Or paste CSV content manually...
                  </summary>
                  <textarea
                    value={closedImportContent}
                    onChange={(e) => setClosedImportContent(e.target.value)}
                    placeholder="Symbol(CUSIP),Security Description,Date Acquired,Date Sold,Quantity,Cost Basis,Proceeds,Short Term Gain/Loss,Long Term Gain/Loss"
                    rows={6}
                    className="mt-2 w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white font-mono text-xs"
                  />
                </details>
                
                {/* Preview of loaded content */}
                {closedImportContent && (
                  <div className="mt-3 text-xs text-slate-500">
                    📄 {closedImportContent.split('\n').length} lines loaded
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleClosedImport}
                  disabled={closedImporting || !closedImportContent.trim()}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 rounded-lg text-white transition-colors"
                >
                  {closedImporting ? 'Importing...' : 'Import Realized Gains'}
                </button>
                <button
                  onClick={() => setShowClosedImport(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <p>📋 <strong>How to export from Fidelity:</strong></p>
                <ol className="list-decimal list-inside ml-2 space-y-0.5">
                  <li>Go to Positions → "Realized gain/loss"</li>
                  <li>Select "All Accounts" and date range (e.g., YTD)</li>
                  <li>Click Download/Export as CSV</li>
                </ol>
                <p className="mt-2">📊 This goes directly to your Realized Gains history. Use "Reconcile Sales" if you want to match specific lots.</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Import Results */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-emerald-900/30 border border-emerald-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-emerald-400">{closedImportResult.imported}</div>
                  <div className="text-sm text-slate-400">Imported</div>
                </div>
                <div className="bg-amber-900/30 border border-amber-700/50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-amber-400">{closedImportResult.skipped || 0}</div>
                  <div className="text-sm text-slate-400">Skipped (duplicates)</div>
                </div>
                <div className={`${closedImportResult.total_gain_loss >= 0 ? 'bg-emerald-900/30 border-emerald-700/50' : 'bg-red-900/30 border-red-700/50'} border rounded-lg p-4 text-center`}>
                  <div className={`text-2xl font-bold ${closedImportResult.total_gain_loss >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {closedImportResult.total_gain_loss >= 0 ? '+' : ''}${closedImportResult.total_gain_loss?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </div>
                  <div className="text-sm text-slate-400">Total G/L Imported</div>
                </div>
              </div>
              
              <button
                onClick={clearClosedImport}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
              >
                Done
              </button>
            </div>
          )}
        </div>
      )}

      {/* Reconciliation Panel */}
      {showReconcile && (
        <div className="bg-slate-800/50 border border-amber-700/50 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">Reconcile Closed Lots (Sales)</h3>
          <p className="text-sm text-slate-400 mb-4">
            Export "Realized Gains" CSV from Fidelity and paste below to match & close lots.
          </p>
          
          {!reconcileResults ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Target Account</label>
                <select
                  value={reconcileAccount}
                  onChange={(e) => setReconcileAccount(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white"
                >
                  <option value="fidelity_brokeragelink">BrokerageLink (401k)</option>
                  <option value="fidelity_taxable">Fidelity Taxable</option>
                  <option value="fidelity_rollover_ira">Rollover IRA</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">
                  Paste Closed Lots CSV (from Fidelity Realized Gains export)
                </label>
                <textarea
                  value={reconcileContent}
                  onChange={(e) => setReconcileContent(e.target.value)}
                  placeholder="Symbol(CUSIP),Security Description,Date Acquired,Date Sold,Quantity,Cost Basis,Proceeds,Short Term Gain/Loss,Long Term Gain/Loss"
                  rows={6}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-2 text-white font-mono text-sm"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => handleReconcile(false)}
                  disabled={reconciling || !reconcileContent.trim()}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-600 rounded-lg text-white transition-colors"
                >
                  {reconciling ? 'Analyzing...' : 'Preview Matches'}
                </button>
                <button
                  onClick={() => setShowReconcile(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
              <div className="text-xs text-slate-500 space-y-1">
                <p>📋 <strong>How to export from Fidelity:</strong></p>
                <ol className="list-decimal list-inside ml-2 space-y-0.5">
                  <li>Go to Positions page</li>
                  <li>Click "Realized gain/loss" link</li>
                  <li>Select date range</li>
                  <li>Click Download/Export as CSV</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Results Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-slate-900 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-white">{reconcileResults.total_closed_lots}</div>
                  <div className="text-xs text-slate-400">Closed Lots in CSV</div>
                </div>
                <div className="bg-green-900/30 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-green-400">{reconcileResults.matched}</div>
                  <div className="text-xs text-slate-400">Matched</div>
                </div>
                <div className="bg-amber-900/30 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-400">{reconcileResults.review_needed}</div>
                  <div className="text-xs text-slate-400">Review Needed</div>
                </div>
                <div className="bg-red-900/30 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-400">{reconcileResults.unmatched}</div>
                  <div className="text-xs text-slate-400">Unmatched</div>
                </div>
              </div>

              {/* Auto-closed info */}
              {reconcileResults.auto_closed > 0 && (
                <div className="bg-green-900/30 border border-green-700 rounded-lg p-3 flex items-center gap-2">
                  <CheckCircle className="text-green-400" size={20} />
                  <span className="text-green-300">
                    Auto-closed {reconcileResults.auto_closed} lots
                  </span>
                </div>
              )}

              {/* Details */}
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {reconcileResults.details?.matched?.slice(0, 10).map((item, i) => (
                  <div key={i} className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CheckCircle className="text-green-400" size={18} />
                      <div>
                        <span className="text-white font-medium">{item.closed_lot.ticker}</span>
                        <span className="text-slate-400 text-sm ml-2">
                          {item.closed_lot.quantity} shares @ ${(item.closed_lot.cost_basis / item.closed_lot.quantity).toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="text-sm">
                      <span className={item.matches[0]?.sale_type === 'partial' ? 'text-amber-400' : 'text-green-400'}>
                        {item.matches[0]?.sale_type === 'partial' ? 'Partial Sale' : 'Full Sale'}
                      </span>
                      <span className="text-slate-500 ml-2">→ Lot #{item.matches[0]?.lot_id}</span>
                    </div>
                  </div>
                ))}
                
                {reconcileResults.details?.unmatched?.slice(0, 5).map((item, i) => (
                  <div key={`u-${i}`} className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <XCircle className="text-red-400" size={18} />
                      <div>
                        <span className="text-white font-medium">{item.closed_lot.ticker}</span>
                        <span className="text-slate-400 text-sm ml-2">
                          {item.closed_lot.quantity} shares (acquired {item.closed_lot.acquired_date})
                        </span>
                      </div>
                    </div>
                    <span className="text-red-400 text-sm">No matching lot found</span>
                  </div>
                ))}

                {reconcileResults.details?.review_needed?.slice(0, 5).map((item, i) => (
                  <div key={`r-${i}`} className="bg-slate-900/50 rounded-lg p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <HelpCircle className="text-amber-400" size={18} />
                      <div>
                        <span className="text-white font-medium">{item.closed_lot.ticker}</span>
                        <span className="text-slate-400 text-sm ml-2">
                          {item.closed_lot.quantity} shares
                        </span>
                      </div>
                    </div>
                    <span className="text-amber-400 text-sm">{item.matches?.length || 0} possible matches</span>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-2 border-t border-slate-700">
                {reconcileResults.matched > 0 && reconcileResults.auto_closed === 0 && (
                  <button
                    onClick={() => handleReconcile(true)}
                    disabled={reconciling}
                    className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white transition-colors"
                  >
                    {reconciling ? 'Closing...' : `Auto-Close ${reconcileResults.matched} Matched Lots`}
                  </button>
                )}
                <button
                  onClick={clearReconciliation}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white transition-colors"
                >
                  {reconcileResults.auto_closed > 0 ? 'Done' : 'Close'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={selectedAccount}
          onChange={(e) => setSelectedAccount(e.target.value)}
          className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
        >
          {accounts.map(acc => (
            <option key={acc.id} value={acc.id}>{acc.label}</option>
          ))}
        </select>
        <input
          type="text"
          value={selectedTicker}
          onChange={(e) => setSelectedTicker(e.target.value.toUpperCase())}
          placeholder="Filter by ticker..."
          className="bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="text-red-400" />
          <span className="text-red-300">{error}</span>
        </div>
      )}

      {/* Lots Table */}
      {loading ? (
        <div className="text-center py-12 text-slate-400">Loading lots...</div>
      ) : tickerSummaries.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          No lots found. Import lots using the browser scraper.
        </div>
      ) : (
        <div className="space-y-2">
          {tickerSummaries.map(({ ticker, lots: tickerLots, totalShares, totalCost, avgCost, shortTermCount, longTermCount, dripCount }) => (
            <div key={ticker} className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
              {/* Ticker Header */}
              <button
                onClick={() => toggleTicker(ticker)}
                className="w-full flex items-center justify-between p-4 hover:bg-slate-700/50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {expandedTickers.has(ticker) ? (
                    <ChevronDown size={20} className="text-slate-400" />
                  ) : (
                    <ChevronRight size={20} className="text-slate-400" />
                  )}
                  <span className="text-lg font-bold text-white">{ticker}</span>
                  <span className="text-sm text-slate-400">
                    {tickerLots.length} lots
                  </span>
                  {dripCount > 0 && (
                    <span className="text-xs bg-purple-900/50 text-purple-300 px-2 py-0.5 rounded">
                      {dripCount} DRIP
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-right">
                    <div className="text-slate-400">Shares</div>
                    <div className="text-white font-medium">{totalShares.toFixed(3)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-400">Cost Basis</div>
                    <div className="text-white font-medium">{formatCurrency(totalCost)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-slate-400">Avg Cost</div>
                    <div className="text-white font-medium">{formatCurrency(avgCost)}</div>
                  </div>
                  <div className="flex gap-2">
                    {shortTermCount > 0 && (
                      <span className="flex items-center gap-1 text-xs bg-amber-900/50 text-amber-300 px-2 py-0.5 rounded">
                        <TrendingDown size={12} />
                        {shortTermCount} ST
                      </span>
                    )}
                    {longTermCount > 0 && (
                      <span className="flex items-center gap-1 text-xs bg-green-900/50 text-green-300 px-2 py-0.5 rounded">
                        <TrendingUp size={12} />
                        {longTermCount} LT
                      </span>
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded Lots */}
              {expandedTickers.has(ticker) && (
                <div className="border-t border-slate-700">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-900/50">
                        <th className="text-left px-4 py-2 text-xs text-slate-400">Acquired</th>
                        <th className="text-left px-4 py-2 text-xs text-slate-400">Account</th>
                        <th className="text-right px-4 py-2 text-xs text-slate-400">Shares</th>
                        <th className="text-right px-4 py-2 text-xs text-slate-400">Cost/Share</th>
                        <th className="text-right px-4 py-2 text-xs text-slate-400">Total Cost</th>
                        <th className="text-center px-4 py-2 text-xs text-slate-400">Term</th>
                        <th className="text-center px-4 py-2 text-xs text-slate-400">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickerLots
                        .sort((a, b) => new Date(a.acquired_date) - new Date(b.acquired_date))
                        .map((lot) => (
                          <tr key={lot.id} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                            <td className="px-4 py-2 text-sm text-white">
                              {formatDate(lot.acquired_date)}
                            </td>
                            <td className="px-4 py-2 text-sm text-slate-300">
                              {lot.account_id.replace('fidelity_', '').replace('_', ' ')}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-white">
                              {lot.remaining_shares.toFixed(4)}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-white">
                              {formatCurrency(lot.cost_basis_per_share)}
                            </td>
                            <td className="px-4 py-2 text-sm text-right text-white">
                              {formatCurrency(lot.remaining_shares * lot.cost_basis_per_share)}
                            </td>
                            <td className="px-4 py-2 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                lot.term === 'long' 
                                  ? 'bg-green-900/50 text-green-300' 
                                  : 'bg-amber-900/50 text-amber-300'
                              }`}>
                                {lot.term === 'long' ? 'Long' : 'Short'}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-center">
                              {lot.is_drip ? (
                                <span className="text-xs bg-purple-900/50 text-purple-300 px-2 py-0.5 rounded">
                                  DRIP
                                </span>
                              ) : (
                                <span className="text-xs text-slate-500">{lot.source}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Summary Footer */}
      {!loading && lots.length > 0 && (
        <div className="bg-slate-800/30 border border-slate-700 rounded-xl p-4">
          <div className="flex justify-between text-sm">
            <div>
              <span className="text-slate-400">Total Cost Basis:</span>
              <span className="text-white font-semibold ml-2">
                {formatCurrency(lots.reduce((sum, l) => sum + (l.remaining_shares * l.cost_basis_per_share), 0))}
              </span>
            </div>
            <div className="flex gap-6">
              <div>
                <span className="text-amber-400">Short-term:</span>
                <span className="text-white ml-2">{lots.filter(l => l.term === 'short').length} lots</span>
              </div>
              <div>
                <span className="text-green-400">Long-term:</span>
                <span className="text-white ml-2">{lots.filter(l => l.term === 'long').length} lots</span>
              </div>
              <div>
                <span className="text-purple-400">DRIP:</span>
                <span className="text-white ml-2">{lots.filter(l => l.is_drip).length} lots</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

